import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, HelpCircle, Loader2, LockKeyhole, Mail, Phone, ShieldCheck, UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '@/core/hooks';
import { getDashboardRoute } from '@/core/utils';
import type { User } from '@/core/types/auth';
import { formatCompanyAddress, useCompanyProfile } from '@/app/utils/companyProfile';

interface LoginResponse {
  user: User;
  token: string;
  expiresAt: string | null;
  sessionTimeoutMinutes: number | null;
}

interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumber: boolean;
  requireSpecial: boolean;
}

interface CandidateRegistrationChallenge {
  challengeId: string;
  expiresAt: string;
  emailDelivery?: { delivered?: boolean; target?: string; message?: string };
  phoneDelivery?: { delivered?: boolean; target?: string; message?: string };
  phoneCodeRequired?: boolean;
  emailCodePreview?: string;
  phoneCodePreview?: string;
  message?: string;
}

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const companyProfile = useCompanyProfile();
  const companyAddress = formatCompanyAddress(companyProfile);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetMode, setResetMode] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [candidateRegistrationOpen, setCandidateRegistrationOpen] = useState(false);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [candidateForm, setCandidateForm] = useState({ fullName: '', email: '', phone: '', roleAppliedFor: 'Care Assistant' });
  const [candidateChallenge, setCandidateChallenge] = useState<CandidateRegistrationChallenge | null>(null);
  const [candidateEmailCode, setCandidateEmailCode] = useState('');
  const [candidatePhoneCode, setCandidatePhoneCode] = useState('');
  const [candidateResult, setCandidateResult] = useState('');
  const [candidateCredentials, setCandidateCredentials] = useState<{ email: string; password: string } | null>(null);
  const [candidateError, setCandidateError] = useState('');
  const [showLoginHelp, setShowLoginHelp] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string; form?: string; resetToken?: string; newPassword?: string }>({});

  useEffect(() => {
    fetch('/api/auth/password-policy')
      .then((response) => response.ok ? response.json() : null)
      .then((payload) => setPasswordPolicy(payload?.data ?? null))
      .catch(() => undefined);
  }, []);

  const validate = () => {
    const nextErrors: { email?: string; password?: string } = {};

    if (!email.trim()) {
      nextErrors.email = 'Email is required';
    } else if (!email.includes('@')) {
      nextErrors.email = 'Please enter a valid email address';
    }

    if (!password) {
      nextErrors.password = 'Password is required';
    } else if (password.length < 6) {
      nextErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const finishLogin = (data: LoginResponse) => {
    if (!data?.user || !data?.token) {
      const message = 'Login response was accepted, but the session data was incomplete. Please try again.';
      setErrors({ form: message });
      toast.error(message);
      return;
    }

    if (data.user.roles.includes('FAMILY')) {
      sessionStorage.setItem('familyMemberId', data.user.id);
      sessionStorage.setItem('clientId', 'client-1');
    } else {
      sessionStorage.removeItem('familyMemberId');
      sessionStorage.removeItem('clientId');
    }

    login(data.user, data.token, {
      expiresAt: data.expiresAt,
      sessionTimeoutMinutes: data.sessionTimeoutMinutes,
    });

    const dashboardRoute = getDashboardRoute(data.user.roles);
    const sessionState = {
      user: data.user,
      token: data.token,
      isAuthenticated: true,
      expiresAt: data.expiresAt,
      sessionTimeoutMinutes: data.sessionTimeoutMinutes,
      lastActivityAt: new Date().toISOString(),
    };

    try {
      window.localStorage.setItem('auth-storage', JSON.stringify({ state: sessionState, version: 0 }));
    } catch {
      // Zustand also persists the session; this fallback only protects browsers with strict storage settings.
    }

    toast.success(`Welcome, ${data.user.name}!`);
    navigate(dashboardRoute, { replace: true });
    window.setTimeout(() => window.location.replace(dashboardRoute), 80);
  };

  const readError = async (response: Response) => {
    const payload = await response.json().catch(() => ({}));
    return payload?.error || payload?.message || 'Request failed';
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = data?.error || 'Invalid email or password.';
        setErrors({ form: message });
        toast.error(message);
        return;
      }

      const loginPayload = data?.data?.user ? data.data : data;
      finishLogin(loginPayload as LoginResponse);
    } finally {
      setIsLoading(false);
    }
  };

  const requestPasswordReset = async () => {
    if (!email.trim() || !email.includes('@')) {
      setErrors({ email: 'Enter your email first' });
      return;
    }

    setIsLoading(true);
    setErrors({});
    try {
      const response = await fetch('/api/auth/request-password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) throw new Error(await readError(response));
      const payload = await response.json();
      toast.success(payload.message || 'If the account exists, a secure reset code has been sent.');
      setResetMode(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Password reset request failed';
      setErrors({ form: message });
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const completePasswordReset = async () => {
    const nextErrors: typeof errors = {};
    if (!resetToken.trim()) nextErrors.resetToken = 'Reset token is required';
    if (!newPassword) nextErrors.newPassword = 'New password is required';
    if (passwordPolicy && newPassword.length < passwordPolicy.minLength) {
      nextErrors.newPassword = `Password must be at least ${passwordPolicy.minLength} characters`;
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setIsLoading(true);
    setErrors({});
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resetToken, newPassword }),
      });
      if (!response.ok) throw new Error(await readError(response));
      toast.success('Password changed. You can sign in now.');
      setResetMode(false);
      setPassword('');
      setNewPassword('');
      setResetToken('');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Password reset failed';
      setErrors({ form: message });
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const startCandidateRegistration = async () => {
    setCandidateError('');
    setCandidateResult('');
    setCandidateCredentials(null);
    if (!candidateForm.fullName.trim()) {
      setCandidateError('Full name is required');
      return;
    }
    if (!candidateForm.email.trim() || !candidateForm.email.includes('@')) {
      setCandidateError('A valid email address is required');
      return;
    }
    if (!candidateForm.phone.trim()) {
      setCandidateError('Phone number is required');
      return;
    }

    setCandidateLoading(true);
    try {
      const response = await fetch('/api/auth/candidate-registration/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(candidateForm),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Could not start candidate registration');
      setCandidateChallenge(payload.data as CandidateRegistrationChallenge);
      toast.success(payload.data?.message || 'Verification started.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not start candidate registration';
      setCandidateError(message);
      toast.error(message);
    } finally {
      setCandidateLoading(false);
    }
  };

  const verifyCandidateRegistration = async () => {
    if (!candidateChallenge) return;
    setCandidateError('');
    setCandidateResult('');
    setCandidateCredentials(null);
    if (!candidateEmailCode.trim()) {
      setCandidateError('Email verification code is required');
      return;
    }
    if (candidateChallenge.phoneCodeRequired && !candidatePhoneCode.trim()) {
      setCandidateError('Phone verification code is required');
      return;
    }

    setCandidateLoading(true);
    try {
      const response = await fetch('/api/auth/candidate-registration/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          challengeId: candidateChallenge.challengeId,
          emailCode: candidateEmailCode,
          phoneCode: candidatePhoneCode,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Could not verify candidate registration');
      if (payload.data?.loginEmail && payload.data?.temporaryPassword) {
        setCandidateCredentials({ email: payload.data.loginEmail, password: payload.data.temporaryPassword });
        setEmail(payload.data.loginEmail);
        setPassword(payload.data.temporaryPassword);
      }
      setCandidateResult(payload.data?.message || 'Registration verified. Check your email for login credentials.');
      setCandidateChallenge(null);
      setCandidateEmailCode('');
      setCandidatePhoneCode('');
      toast.success('Candidate registration completed.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not verify candidate registration';
      setCandidateError(message);
      toast.error(message);
    } finally {
      setCandidateLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 lg:grid lg:grid-cols-[1.05fr_0.95fr]">
      <section className="relative hidden overflow-hidden bg-[#5e3474] lg:flex lg:flex-col lg:justify-between">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.18),transparent_32%),linear-gradient(135deg,rgba(94,52,116,0.96),rgba(37,51,74,0.98))]" />
        <div className="relative px-12 pt-12">
          <img
            src={companyProfile.logoFullUrl}
            alt={companyProfile.publicName}
            className="h-36 w-auto object-contain"
          />
        </div>
        <div className="relative px-12 pb-14 text-white">
          <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-semibold">
            <ShieldCheck className="h-4 w-4" />
            Secure access
          </div>
          <h1 className="max-w-xl text-5xl font-bold leading-tight tracking-normal">
            {companyProfile.brandName}
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-8 text-white/82">
            {companyAddress}
          </p>
          <div className="mt-8 flex items-center gap-3 text-sm font-medium text-white/85">
            <Phone className="h-4 w-4" />
            <span>{companyProfile.phone} | On Call {companyProfile.onCallPhone}</span>
          </div>
        </div>
      </section>

      <main className="flex min-h-screen items-center justify-center px-5 py-10 sm:px-8">
        <div className="w-full max-w-md">
          <div className="rounded-lg border border-slate-200 bg-white shadow-xl">
            <div className="space-y-3 px-8 pb-4 pt-8 text-center">
              <div className="flex justify-center">
                <img
                  src={companyProfile.logoMarkUrl}
                  alt={companyProfile.publicName}
                  className="h-20 w-20 rounded-lg object-contain"
                />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-950">{companyProfile.brandName}</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Sign in to the home care management application
                </p>
                <div className="mt-4 rounded-md bg-slate-50 px-4 py-3 text-left text-xs leading-5 text-slate-600">
                  <p className="font-semibold text-slate-800">
                    {companyAddress}
                  </p>
                  <p>Tel: {companyProfile.phone} | On Call: {companyProfile.onCallPhone}</p>
                  <p>Email: {companyProfile.email}</p>
                  <p>{companyProfile.openingHours.weekdayOffice}</p>
                </div>
              </div>
            </div>

            <div className="px-8 pb-8">
              <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium text-slate-800">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      id="email"
                      name="fuchsia-login-email"
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder=""
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="none"
                      spellCheck={false}
                      disabled={isLoading}
                      className="h-11 w-full rounded-md border border-slate-300 bg-white pl-10 pr-3 text-sm text-slate-950 outline-none transition focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20 disabled:cursor-not-allowed disabled:bg-slate-50"
                    />
                  </div>
                  {errors.email && <p className="text-sm text-red-600">{errors.email}</p>}
                </div>

                <div className="space-y-2">
                  <label htmlFor="password" className="text-sm font-medium text-slate-800">
                    Password
                  </label>
                  <div className="relative">
                    <LockKeyhole className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      id="password"
                      name="fuchsia-login-password"
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder="Enter your password"
                      autoComplete="new-password"
                      disabled={isLoading}
                      className="h-11 w-full rounded-md border border-slate-300 bg-white pl-10 pr-3 text-sm text-slate-950 outline-none transition focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20 disabled:cursor-not-allowed disabled:bg-slate-50"
                    />
                  </div>
                  {errors.password && <p className="text-sm text-red-600">{errors.password}</p>}
                </div>

                {showLoginHelp && (
                  <div className="rounded-md border border-purple-200 bg-purple-50 p-4 text-left text-xs leading-5 text-slate-700">
                    <p className="font-bold text-slate-950">How to sign in</p>
                    <p className="mt-1">
                      Enter your registered email and password, then press Sign in. If you cannot access your account, use secure password reset or call the office.
                    </p>
                    <p className="mt-2 font-semibold text-[#6c3a80]">
                      If you cannot access your account, call the office or use secure password reset.
                    </p>
                  </div>
                )}

                {resetMode && (
                  <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50 p-4">
                    <p className="text-sm font-semibold text-amber-950">Secure password reset</p>
                    <p className="text-xs leading-5 text-amber-900">
                      Enter the secure reset code received by email or SMS. The code is not displayed in the browser.
                    </p>
                    <div>
                      <label className="text-xs font-medium text-amber-900">Reset token</label>
                      <input
                        value={resetToken}
                        onChange={(event) => setResetToken(event.target.value)}
                        className="mt-1 h-10 w-full rounded-md border border-amber-300 bg-white px-3 text-sm text-slate-950"
                      />
                      {errors.resetToken && <p className="mt-1 text-xs text-red-600">{errors.resetToken}</p>}
                    </div>
                    <div>
                      <label className="text-xs font-medium text-amber-900">New password</label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(event) => setNewPassword(event.target.value)}
                        className="mt-1 h-10 w-full rounded-md border border-amber-300 bg-white px-3 text-sm text-slate-950"
                      />
                      {errors.newPassword && <p className="mt-1 text-xs text-red-600">{errors.newPassword}</p>}
                    </div>
                    {passwordPolicy && (
                      <p className="text-xs leading-5 text-amber-900">
                        Policy: minimum {passwordPolicy.minLength} characters, uppercase, lowercase, number and special character.
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={completePasswordReset}
                      disabled={isLoading}
                      className="h-10 w-full rounded-md bg-amber-700 px-4 text-sm font-semibold text-white hover:bg-amber-800 disabled:opacity-60"
                    >
                      Save new password
                    </button>
                  </div>
                )}

                {errors.form && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {errors.form}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex h-11 w-full items-center justify-center rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#5e3474] focus:outline-none focus:ring-2 focus:ring-[#6c3a80] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Sign in
                </button>
              </form>

              <div className="mt-5 rounded-lg border border-purple-200 bg-purple-50/70 p-4">
                <button
                  type="button"
                  onClick={() => setCandidateRegistrationOpen((current) => !current)}
                  className="flex w-full items-center justify-between gap-3 text-left"
                  aria-expanded={candidateRegistrationOpen}
                >
                  <span>
                    <span className="flex items-center gap-2 text-sm font-bold text-slate-950">
                      <UserPlus className="h-4 w-4 text-[#6c3a80]" />
                      New candidate registration
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-slate-600">
                      Register with email and phone, verify the code, then receive temporary candidate portal credentials.
                    </span>
                  </span>
                  <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-[#6c3a80]">
                    {candidateRegistrationOpen ? 'Close' : 'Open'}
                  </span>
                </button>

                {candidateRegistrationOpen && (
                  <div className="mt-4 space-y-3">
                    {!candidateChallenge && !candidateResult && (
                      <>
                        <label className="block space-y-1">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Full name</span>
                          <input
                            value={candidateForm.fullName}
                            onChange={(event) => setCandidateForm((current) => ({ ...current, fullName: event.target.value }))}
                            className="h-10 w-full rounded-md border border-purple-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-[#6c3a80]"
                            disabled={candidateLoading}
                          />
                        </label>
                        <label className="block space-y-1">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</span>
                          <input
                            type="email"
                            value={candidateForm.email}
                            onChange={(event) => setCandidateForm((current) => ({ ...current, email: event.target.value }))}
                            className="h-10 w-full rounded-md border border-purple-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-[#6c3a80]"
                            disabled={candidateLoading}
                          />
                        </label>
                        <label className="block space-y-1">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Phone number</span>
                          <input
                            value={candidateForm.phone}
                            onChange={(event) => setCandidateForm((current) => ({ ...current, phone: event.target.value }))}
                            placeholder="Example: 07700 900000"
                            className="h-10 w-full rounded-md border border-purple-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-[#6c3a80]"
                            disabled={candidateLoading}
                          />
                        </label>
                        <label className="block space-y-1">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Role applied for</span>
                          <select
                            value={candidateForm.roleAppliedFor}
                            onChange={(event) => setCandidateForm((current) => ({ ...current, roleAppliedFor: event.target.value }))}
                            className="h-10 w-full rounded-md border border-purple-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-[#6c3a80]"
                            disabled={candidateLoading}
                          >
                            <option>Care Assistant</option>
                            <option>Senior Carer</option>
                            <option>Care Coordinator</option>
                            <option>Care Supervisor</option>
                            <option>Recruitment and HR Officer</option>
                            <option>Driver</option>
                          </select>
                        </label>
                        <button
                          type="button"
                          onClick={() => void startCandidateRegistration()}
                          disabled={candidateLoading}
                          className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white hover:bg-[#5e3474] disabled:opacity-60"
                        >
                          {candidateLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                          Start registration
                        </button>
                      </>
                    )}

                    {candidateChallenge && (
                      <div className="space-y-3 rounded-md border border-purple-200 bg-white p-3">
                        <p className="text-sm font-semibold text-slate-950">Verify registration</p>
                        <p className="text-xs leading-5 text-slate-600">
                          Enter the code sent to {candidateChallenge.emailDelivery?.target || 'your email'}.
                          {candidateChallenge.phoneCodeRequired ? ` A phone code was also sent to ${candidateChallenge.phoneDelivery?.target || 'your phone'}.` : ' SMS verification will be used automatically when the SMS provider is configured.'}
                        </p>
                        {(candidateChallenge.emailCodePreview || candidateChallenge.phoneCodePreview) && (
                          <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs leading-5 text-amber-900">
                            <p className="font-semibold">Setup verification codes</p>
                            {candidateChallenge.emailCodePreview && (
                              <p>Email code: <span className="font-mono font-bold">{candidateChallenge.emailCodePreview}</span></p>
                            )}
                            {candidateChallenge.phoneCodePreview && (
                              <p>Phone code: <span className="font-mono font-bold">{candidateChallenge.phoneCodePreview}</span></p>
                            )}
                          </div>
                        )}
                        <label className="block space-y-1">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email code</span>
                          <input
                            value={candidateEmailCode}
                            onChange={(event) => setCandidateEmailCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                            inputMode="numeric"
                            className="h-10 w-full rounded-md border border-purple-200 bg-white px-3 text-center text-base font-semibold tracking-[0.25em] text-slate-950 outline-none focus:border-[#6c3a80]"
                            disabled={candidateLoading}
                          />
                        </label>
                        {candidateChallenge.phoneCodeRequired && (
                          <label className="block space-y-1">
                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Phone code</span>
                            <input
                              value={candidatePhoneCode}
                              onChange={(event) => setCandidatePhoneCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                              inputMode="numeric"
                              className="h-10 w-full rounded-md border border-purple-200 bg-white px-3 text-center text-base font-semibold tracking-[0.25em] text-slate-950 outline-none focus:border-[#6c3a80]"
                              disabled={candidateLoading}
                            />
                          </label>
                        )}
                        <button
                          type="button"
                          onClick={() => void verifyCandidateRegistration()}
                          disabled={candidateLoading}
                          className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white hover:bg-[#5e3474] disabled:opacity-60"
                        >
                          {candidateLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                          Verify and create login
                        </button>
                      </div>
                    )}

                    {candidateResult && (
                      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm leading-6 text-emerald-900">
                        <p className="flex items-center gap-2 font-bold"><CheckCircle2 className="h-4 w-4" /> Registration completed</p>
                        <p className="mt-1">{candidateResult}</p>
                        {candidateCredentials && (
                          <div className="mt-2 rounded bg-white p-2 text-xs text-slate-700">
                            <p><strong>Email:</strong> {candidateCredentials.email}</p>
                            <p><strong>Temporary password:</strong> {candidateCredentials.password}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {candidateError && (
                      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                        {candidateError}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-5 grid gap-2 sm:grid-cols-2">
                <a
                  href={`tel:${companyProfile.phone}`}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#6c3a80] bg-white px-4 text-sm font-semibold text-[#6c3a80] hover:bg-[#fbf7fd]"
                >
                  <Phone className="h-4 w-4" />
                  Call office
                </a>
                <button
                  type="button"
                  onClick={() => setShowLoginHelp((current) => !current)}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  aria-expanded={showLoginHelp}
                >
                  <HelpCircle className="h-4 w-4" />
                  Login help
                </button>
              </div>

              <div className="mt-4 flex flex-col items-center gap-2 text-center text-sm text-slate-500">
                <button
                  type="button"
                  onClick={requestPasswordReset}
                  disabled={isLoading}
                  className="font-medium text-[#6c3a80] hover:underline disabled:opacity-60"
                >
                  Forgot password / secure reset
                </button>
                <span>
                  Need access?{' '}
                <a href={`tel:${companyProfile.phone}`} className="font-medium text-[#6c3a80] hover:underline">
                  Contact the administrator
                </a>
                </span>
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

